import {
  BASE_PALETTES,
  type BaseThemePalette,
  type ThemeName,
} from '@bn-games/theming';

export type { ThemeName };

/**
 * Sudoku theme: extends the cross-game base palette with board-specific
 * tokens. The renderer (task 12) reads these directly.
 *
 * - `boardBackground` paints the playable 9x9 surface.
 * - `gridLineThin` / `gridLineThick` separate cells / 3x3 boxes.
 * - `givenDigit` paints immutable starting digits; `userDigit` paints the
 *   player's entries; `pencilMark` paints candidate notes.
 * - `selectedCell` is the soft fill behind the focused cell.
 * - `sameDigitHighlight` is the dim wash applied to other cells holding the
 *   same digit as the selection.
 * - `errorFlash` paints conflicting digits when highlight-errors is on.
 * - `numpadBackground` is the soft fill over the bottom numpad strip.
 * - `numpadButton` / `numpadButtonActive` / `numpadButtonPressed` /
 *   `numpadButtonDisabled` fill the rounded-rect button bodies in their
 *   four visual states.
 * - `numpadDigit` paints the digit / icon glyph; `numpadDigitDisabled` is
 *   the dimmer text used when a digit count has hit 9.
 */
export interface Theme extends BaseThemePalette {
  readonly monogram: string;

  readonly boardBackground: string;
  readonly boardBorder: string;

  readonly gridLineThin: string;
  readonly gridLineThick: string;

  readonly givenDigit: string;
  readonly userDigit: string;
  readonly pencilMark: string;

  readonly selectedCell: string;
  readonly sameDigitHighlight: string;
  readonly errorFlash: string;

  readonly numpadBackground: string;
  readonly numpadButton: string;
  readonly numpadButtonActive: string;
  readonly numpadButtonPressed: string;
  readonly numpadButtonDisabled: string;
  readonly numpadDigit: string;
  readonly numpadDigitDisabled: string;
}

export const MAHOGANY_THEME: Theme = {
  ...BASE_PALETTES.mahogany,
  monogram: 'S',

  boardBackground: '#f4e8ce',
  boardBorder: '#5c3a18',

  gridLineThin: 'rgba(60, 30, 12, 0.28)',
  gridLineThick: '#3d1808',

  givenDigit: '#1a0a04',
  userDigit: '#7a3a1a',
  pencilMark: 'rgba(60, 30, 12, 0.55)',

  selectedCell: 'rgba(244, 216, 148, 0.55)',
  sameDigitHighlight: 'rgba(202, 160, 90, 0.22)',
  errorFlash: 'rgba(190, 50, 30, 0.65)',

  numpadBackground: 'rgba(60, 30, 12, 0.12)',
  numpadButton: '#3b1f12',
  numpadButtonActive: '#caa05a',
  numpadButtonPressed: '#5a3018',
  numpadButtonDisabled: 'rgba(59, 31, 18, 0.45)',
  numpadDigit: '#f0e2c2',
  numpadDigitDisabled: 'rgba(240, 226, 194, 0.35)',
};

export const PERSIAN_THEME: Theme = {
  ...BASE_PALETTES.persian,
  monogram: 'S',

  boardBackground: '#ece2c0',
  boardBorder: '#1a2f44',

  gridLineThin: 'rgba(20, 35, 55, 0.28)',
  gridLineThick: '#0b1825',

  givenDigit: '#0a1820',
  userDigit: '#1a4858',
  pencilMark: 'rgba(20, 35, 55, 0.55)',

  selectedCell: 'rgba(58, 166, 176, 0.32)',
  sameDigitHighlight: 'rgba(212, 165, 90, 0.22)',
  errorFlash: 'rgba(190, 50, 30, 0.65)',

  numpadBackground: 'rgba(20, 35, 55, 0.12)',
  numpadButton: '#1a2f44',
  numpadButtonActive: '#3aa6b0',
  numpadButtonPressed: '#26425a',
  numpadButtonDisabled: 'rgba(26, 47, 68, 0.45)',
  numpadDigit: '#ece2c0',
  numpadDigitDisabled: 'rgba(236, 226, 192, 0.35)',
};

export const DECO_THEME: Theme = {
  ...BASE_PALETTES.deco,
  monogram: 'S',

  boardBackground: '#141210',
  boardBorder: '#d4b06c',

  gridLineThin: 'rgba(212, 176, 108, 0.18)',
  gridLineThick: '#d4b06c',

  givenDigit: '#f2ecdc',
  userDigit: '#d4b06c',
  pencilMark: 'rgba(242, 236, 220, 0.45)',

  selectedCell: 'rgba(240, 216, 152, 0.18)',
  sameDigitHighlight: 'rgba(212, 176, 108, 0.14)',
  errorFlash: 'rgba(220, 80, 60, 0.7)',

  numpadBackground: 'rgba(212, 176, 108, 0.08)',
  numpadButton: '#161412',
  numpadButtonActive: '#d4b06c',
  numpadButtonPressed: '#2a2724',
  numpadButtonDisabled: 'rgba(22, 20, 18, 0.55)',
  numpadDigit: '#f2ecdc',
  numpadDigitDisabled: 'rgba(242, 236, 220, 0.30)',
};

export const MONTE_THEME: Theme = {
  ...BASE_PALETTES.monte,
  monogram: 'S',

  boardBackground: '#f2ecd8',
  boardBorder: '#0f2418',

  gridLineThin: 'rgba(15, 36, 24, 0.28)',
  gridLineThick: '#06150c',

  givenDigit: '#0a0a0a',
  userDigit: '#0f3a26',
  pencilMark: 'rgba(15, 36, 24, 0.55)',

  selectedCell: 'rgba(219, 84, 64, 0.28)',
  sameDigitHighlight: 'rgba(212, 176, 108, 0.22)',
  errorFlash: 'rgba(219, 84, 64, 0.7)',

  numpadBackground: 'rgba(15, 36, 24, 0.12)',
  numpadButton: '#0f2418',
  numpadButtonActive: '#db5440',
  numpadButtonPressed: '#173628',
  numpadButtonDisabled: 'rgba(15, 36, 24, 0.45)',
  numpadDigit: '#f2ecd8',
  numpadDigitDisabled: 'rgba(242, 236, 216, 0.30)',
};

export const THEMES: Record<ThemeName, Theme> = {
  mahogany: MAHOGANY_THEME,
  persian: PERSIAN_THEME,
  deco: DECO_THEME,
  monte: MONTE_THEME,
};
